import { InputNumber, Row } from 'antd';
import React, { useEffect, useMemo, useState } from 'react'
import formatMsg from '../../util/formatMsg';
import Helper from '../../util/helper';
import { DayPicker } from 'react-day-picker';
import { colors, timeutil } from 'sharedresources';
import { getInstanceClone } from '../../util/timeutil';
import { limitWindows } from '../../util/limitWindows';
import { isMonthDaysPerWeekPack } from '../../util/pricingPackWeekdays';
import { CalendarOutlined, ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons';

const BookingCalendar = ({
	selectSlotsLater = false, selectedPricingPack, maxFrequency = {},
	credits = 0, setCredits = null, selectedBatch, selectedMonths,
	currentMonth, setSelectedMonths, setSelectedDays, disabled,
	selectedWeeks, setSelectedWeeks, selectedDays, setCurrentMonth,
	selectedWeekDays, setSelectedWeekDays, setSelectedTimings,
	firebase, handleTimingClick, groupedTimings, selectedTimings,
	viewAll = false, readOnly = false, hideTimings = false, hideDays = false,
	setOpenActivityId, packageActivityMap, batchSlotMap, centerId, maxCapacityPerBatchCenter, setCommonAvailability,
	commonAvailability,
	allowWaitlist,
	waitlistCapacity,
	previouslySelectedSlots = [],
	enrollmentRules,
	// Count the session cap per day/week/month, per billing period or for the whole pack (see
	// util/limitWindows). Off for bookings made before that counting existed, which keep the
	// "Pack runs for" windows they were booked under.
	periodCounting = false,
	limitAnchor = null,
}: any) => {
	const [enrollmentError, setEnrollmentError] = useState<{ dateList: string; durationText: string } | null>(null);

	const start = (selectedBatch?.startDate ? timeutil.getInstance(selectedBatch?.startDate) : timeutil.getInstance(Math.min(...selectedBatch?.intervals.map(d => timeutil.getEpoch(timeutil.getInstance(d.date)))))).startOf("month")
	const end = (selectedBatch?.endDate ? timeutil.getInstance(selectedBatch?.endDate) : timeutil.getInstance(Math.max(...selectedBatch?.intervals.map(d => timeutil.getEpoch(timeutil.getInstance(d.date)))))).endOf("month")

	const allMonths: any[] = [];
	let current = start;
	while (timeutil.isBefore(current, end) || timeutil.isSame(current, end, "month")) {
		allMonths.push(getInstanceClone(current));
		current = current.add(1, "month");
	}


	const weeksInMonth = useMemo(() => {
		let weeks = [];
		let firstDayOfMonth = getInstanceClone(currentMonth).startOf("month");
		let lastDayOfMonth = getInstanceClone(currentMonth).endOf("month");
		let startOfWeek = timeutil.getStartOfWeek(firstDayOfMonth, selectedBatch?.weekStartDay);
		while (startOfWeek.isBefore(lastDayOfMonth)) {
			let weekDates: Date[] = [];

			for (let i = 0; i < 7; i++) {
				const currentDate = getInstanceClone(startOfWeek).add(i, "day");
				weekDates.push(Helper.convertToDate(currentDate, firebase));
			}
			if (weekDates.some(date => timeutil.getInstance(date).month() === currentMonth.month())) {
				weeks.push({
					label: `Week+${timeutil.getEpoch(startOfWeek)}`,
					dates: weekDates
				});
			}
			startOfWeek = getInstanceClone(startOfWeek).add(1, "week");
		}
		return weeks;
	}, [currentMonth, selectedBatch?.weekStartDay]);

	const toggleMonthSelection = (month: any) => {
		const isSelected = selectedMonths.some((m) => timeutil.isSame(m, month, "month"));

		// For CUSTOM with DURATION type and durationUnit is months, auto-select n consecutive months
		const isCustomDurationMonths = selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
			selectedPricingPack?.pricingPack?.durationType === "DURATION" &&
			selectedPricingPack?.pricingPack?.durationUnit?.toLowerCase() === "months" &&
			selectedPricingPack?.pricingPack?.durationValue;

		let monthsToToggle = [];
		if (isCustomDurationMonths && !isSelected) {
			// Select n consecutive months starting from the clicked month
			const durationValue = selectedPricingPack.pricingPack.durationValue;
			for (let i = 0; i < durationValue; i++) {
				const monthToAdd = getInstanceClone(month).startOf("month").add(i, "month");
				// Only add if the month exists in allMonths (within allowed range)
				if (allMonths.some((m) => timeutil.isSame(m, monthToAdd, "month"))) {
					monthsToToggle.push(getInstanceClone(monthToAdd));
				}
			}
		} else if (isCustomDurationMonths && isSelected) {
			// Deselect the entire duration period that contains the clicked month
			// Find which duration period this month belongs to
			const durationValue = selectedPricingPack.pricingPack.durationValue;
			const sortedMonths = [...selectedMonths].sort((a, b) => timeutil.getEpoch(a) - timeutil.getEpoch(b));

			// Find all duration periods in selected months
			const durationPeriods: any[][] = [];
			const usedMonths = new Set<string>();

			for (const selectedMonth of sortedMonths) {
				const monthKey = selectedMonth.format("YYYY-MM");
				if (usedMonths.has(monthKey)) continue;

				let isPartOfExistingPeriod = false;
				for (const period of durationPeriods) {
					const periodStart = period[0];
					const periodEnd = getInstanceClone(periodStart).add(durationValue - 1, "month");

					if ((timeutil.isSame(selectedMonth, periodStart, "month") || timeutil.isAfter(selectedMonth, periodStart, "month")) &&
						(timeutil.isSame(selectedMonth, periodEnd, "month") || timeutil.isBefore(selectedMonth, periodEnd, "month"))) {
						period.push(selectedMonth);
						usedMonths.add(monthKey);
						isPartOfExistingPeriod = true;
						break;
					}
				}

				if (!isPartOfExistingPeriod) {
					const newPeriod: any[] = [selectedMonth];
					usedMonths.add(monthKey);

					for (let i = 1; i < durationValue; i++) {
						const nextMonth = getInstanceClone(selectedMonth).add(i, "month");
						const nextMonthKey = nextMonth.format("YYYY-MM");
						if (sortedMonths.some(m => timeutil.isSame(m, nextMonth, "month")) && !usedMonths.has(nextMonthKey)) {
							newPeriod.push(nextMonth);
							usedMonths.add(nextMonthKey);
						}
					}
					durationPeriods.push(newPeriod);
				}
			}

			// Find the period that contains the clicked month
			for (const period of durationPeriods) {
				const periodStart = period[0];
				const periodEnd = getInstanceClone(periodStart).add(durationValue - 1, "month");

				if ((timeutil.isSame(month, periodStart, "month") || timeutil.isAfter(month, periodStart, "month")) &&
					(timeutil.isSame(month, periodEnd, "month") || timeutil.isBefore(month, periodEnd, "month"))) {
					// This period contains the clicked month - deselect all months in this period
					monthsToToggle = period.map(m => getInstanceClone(m));
					break;
				}
			}

			if (monthsToToggle.length === 0) {
				for (let i = 0; i < durationValue; i++) {
					const monthToRemove = getInstanceClone(month).startOf("month").add(i, "month");
					if (selectedMonths.some((m) => timeutil.isSame(m, monthToRemove, "month"))) {
						monthsToToggle.push(getInstanceClone(monthToRemove));
					}
				}
			}
		} else {
			// Default behavior: just toggle the single month
			monthsToToggle = [getInstanceClone(month).startOf("month")];
		}

		setSelectedMonths(
			isSelected
				? selectedMonths.filter((m) => !monthsToToggle.some((mt) => timeutil.isSame(m, mt, "month")))
				: [...selectedMonths, ...monthsToToggle.filter((mt) => !selectedMonths.some((m) => timeutil.isSame(m, mt, "month")))]
		);

		const ws = selectedBatch?.weekStartDay;
		const boundaryWeekEpochs = new Set<number>();
		monthsToToggle.forEach((mt) => {
			boundaryWeekEpochs.add(timeutil.getEpoch(timeutil.getStartOfWeek(getInstanceClone(mt).startOf('month'), ws)));
			boundaryWeekEpochs.add(timeutil.getEpoch(timeutil.getStartOfWeek(getInstanceClone(mt).endOf('month'), ws)));
		});
		const sameWeekAsMonthBoundary = (epochOrDate) =>
			boundaryWeekEpochs.has(timeutil.getEpoch(timeutil.getStartOfWeek(epochOrDate, ws)));

		let weekSelected;
		selectedWeeks.forEach(m => {
			if (sameWeekAsMonthBoundary(Number(m.split("+")[1]))) weekSelected = true
		})
		setSelectedWeeks(prev => {
			const check = (m) => !sameWeekAsMonthBoundary(Number(m.split("+")[1]))
			return isSelected
				? prev.filter((m) => !timeutil.isSame(Number(m.split("+")[1]), month, "month") && check(m))
				: prev.filter(m => check(m))
		});
		setSelectedDays((prev) => {
			if (weekSelected) {
				prev = prev.filter(d => !sameWeekAsMonthBoundary(d))
			}
			if (isSelected) {
				// Remove days from all months being deselected
				return prev.filter((d) => !monthsToToggle.some((mt) => timeutil.isSame(timeutil.getInstance(d), mt, "month"))).sort((a, b) => a.getTime() - b.getTime());
			} else if (selectedPricingPack?.pricingPack.all || isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack)) {
				// Add all days from all months being selected (days-per-week packs are narrowed by the weekday picker)
				const allDays = [];
				monthsToToggle.forEach((mt) => {
					const monthStart = getInstanceClone(mt).startOf("month");
					const monthEnd = getInstanceClone(mt).endOf("month");
					for (let date = getInstanceClone(monthStart); timeutil.isBefore(date, monthEnd) || timeutil.isSame(date, monthEnd, "day"); date = getInstanceClone(date).add(1, "day")) {
						if (!disabled(date)) {
							allDays.push(Helper.convertToDate(date, firebase));
						}
					}
				});
				return [...prev, ...allDays].sort((a, b) => a.getTime() - b.getTime());
			}
			return prev.sort((a, b) => a.getTime() - b.getTime());
		});
	};

	const toggleWeekSelection = (weekLabel: string, weekDates: Date[]) => {
		if (
			selectedPricingPack?.pricingPack?.frequency === "MONTH" &&
			selectedPricingPack?.pricingPack?.frequencyType === "WEEKS" &&
			selectedPricingPack?.pricingPack?.frequencyValue
		) {
			const [, timestampStr] = weekLabel.split("+");
			const weekStart = timeutil.getInstance(Number(timestampStr));
			const monthKey = weekStart.format("YYYY-MM");

			const monthWiseWeekCount = selectedWeeks.reduce((acc, label) => {
				const [, weekTimestampStr] = label.split("+");
				const weekMonth = timeutil.getInstance(Number(weekTimestampStr)).format("YYYY-MM");
				acc[weekMonth] = (acc[weekMonth] || 0) + 1;
				return acc;
			}, {} as Record<string, number>);
			if (
				!selectedWeeks.includes(weekLabel) &&
				monthWiseWeekCount[monthKey] >= selectedPricingPack.pricingPack.frequencyValue
			) {
				return;
			}
		}

		setSelectedDays((prev) => {
			const validWeekDates = weekDates.filter((date) => !disabled(date));

			if (validWeekDates.length) {
				setSelectedWeeks((prevWeeks) => {
					if (prevWeeks.includes(weekLabel)) {
						return prevWeeks.filter((w) => w !== weekLabel);
					} else {
						return [...prevWeeks, weekLabel];
					}
				});
			}

			const allDatesSelected = validWeekDates.every((date) =>
				prev.some((d) => timeutil.isSame(timeutil.getInstance(d), date, "day"))
			);

			return allDatesSelected
				? prev.filter((d) => !validWeekDates.some((wd) => timeutil.isSame(timeutil.getInstance(d), wd, "day"))).sort((a, b) => a.getTime() - b.getTime())
				: [...prev, ...validWeekDates].sort((a, b) => a.getTime() - b.getTime());
		});
	};

	useEffect(() => {
		getAvailabilityForSelectedSlots();
	}, [selectedDays, selectedTimings]);

	// Monthly days-per-week: every bookable day of each selected month stays in selectedDays and the
	// weekday picker narrows it. Booking/reschedule prefill only carries the already-booked dates, so
	// add the rest (never remove — month deselection is handled by toggleMonthSelection).
	useEffect(() => {
		if (readOnly || !isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack) || !selectedMonths?.length) return;
		const missing: Date[] = [];
		selectedMonths.forEach((m) => {
			const monthEnd = getInstanceClone(m).endOf("month");
			for (let date = getInstanceClone(m).startOf("month"); !timeutil.isAfter(date, monthEnd, "day"); date = getInstanceClone(date).add(1, "day")) {
				if (disabled(date)) continue;
				if (!selectedDays.some((d) => timeutil.isSame(timeutil.getInstance(d), date, "day"))) missing.push(Helper.convertToDate(date, firebase));
			}
		});
		if (missing.length) setSelectedDays([...selectedDays, ...missing].sort((a, b) => a.getTime() - b.getTime()));
	}, [selectedMonths, selectedPricingPack?.pricingPack?.id]);

	// Safety net for CUSTOM + DURATION + WEEKS: if selectedDays carries stale picks
	// from before the consecutive-weeks constraint existed (or via some other path),
	// trim them to the largest valid prefix so the on-screen state always matches
	// the rule. Earliest picks win (they anchor the window).
	useEffect(() => {
		const pricingPack = selectedPricingPack?.pricingPack;
		if (!pricingPack || pricingPack.frequency !== "CUSTOM" || pricingPack.durationType !== "DURATION") return;
		if ((pricingPack.durationUnit || "").toLowerCase() !== "weeks") return;
		if (!pricingPack.durationValue || !selectedDays?.length) return;

		const effectiveCredits = (credits && credits > 0) ? credits : 1;
		const sortedSelected = [...selectedDays].sort((a, b) => a.getTime() - b.getTime());
		const valid: Date[] = [];
		for (const day of sortedSelected) {
			const trial = [...valid, day];
			const trialWindows = calculateWindowsFromDays(trial);
			let ok = trialWindows.length <= effectiveCredits;
			if (ok && pricingPack.noOfClasses && pricingPack.noOfClasses > 0) {
				for (const w of capWindows(trial)) {
					if (w.days.length > pricingPack.noOfClasses) { ok = false; break; }
					if (countClassesInWindow(w) > pricingPack.noOfClasses) { ok = false; break; }
				}
			}
			if (ok) valid.push(day);
		}
		if (valid.length !== selectedDays.length) {
			setSelectedDays(valid);
		}
	}, [selectedPricingPack?.pricingPack?.id, credits]);

	// Automatically check if any slots are bookable for selected days
	useEffect(() => {
		if (!enrollmentRules || !selectedDays?.length || !groupedTimings || Object.keys(groupedTimings).length === 0) {
			setEnrollmentError(null);
			return;
		}

		// For WEEK (and monthly days-per-week) packs, require selectedWeekDays before showing error
		const isWeekPack = (selectedPricingPack?.pricingPack?.frequency === "WEEK" &&
			selectedPricingPack?.pricingPack?.all === false) || isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack);
		if (isWeekPack && !selectedWeekDays?.length) {
			setEnrollmentError(null);
			return;
		}

		const isEnrollmentEnabled = enrollmentRules &&
			enrollmentRules.duration != null &&
			enrollmentRules.durationType &&
			enrollmentRules.duration >= 0;

		if (!isEnrollmentEnabled) {
			setEnrollmentError(null);
			return;
		}

		const currentTime = timeutil.getEpoch(timeutil.getInstance());
		const isRecurring = selectedBatch?.type === "recurring";
		const daysToCheck = getFilteredDaysList();

		if (daysToCheck.length === 0) {
			setEnrollmentError(null);
			return;
		}

		// Check each selected date individually to see if it has at least one bookable slot
		let unbookableDates: Date[] = [];

		// For each selected date, check if there's at least one bookable slot
		daysToCheck.forEach((day) => {
			let hasBookableSlotForThisDay = false;
			const dayOrDateStr = getDayOrDateString(day, isRecurring);

			// Check all activities and their slots for this specific day
			Object.entries(groupedTimings).forEach(([activityId, slotsByDay]: any) => {
				if (!slotsByDay) return;
				if (hasBookableSlotForThisDay) return; // Already found a bookable slot for this day

				Object.entries(slotsByDay).forEach(([dayOrDate, timings]: any) => {
					if (!Array.isArray(timings) || timings.length === 0) return;
					if (hasBookableSlotForThisDay) return; // Already found a bookable slot for this day

					// Check if this dayOrDate is relevant for the current day being checked
					let isRelevant = false;

					if (dayOrDate === "Common slots") {
						isRelevant = true;
					} else if (isRecurring) {
						// For recurring batches, dayOrDate is a day name (e.g., "monday")
						// Check if the current day matches this day name
						const currentDayName = timeutil.inFormat(day, "dddd").toLowerCase();
						isRelevant = currentDayName === dayOrDate.toLowerCase();
					} else {
						// For specific batches, dayOrDate is a formatted date
						// Check if it matches the current day
						const currentDayFormatted = timeutil.getInstance(day).format(Helper.dateFormat(firebase));
						isRelevant = currentDayFormatted === dayOrDate;
					}

					if (!isRelevant) return;

					// Check each timing slot for this day
					timings.forEach((timing: any) => {
						if (hasBookableSlotForThisDay) return; // Already found a bookable slot for this day

						const isBookable = isSlotBookableForDate(
							timing,
							day,
							dayOrDateStr,
							activityId,
							enrollmentRules,
							currentTime,
							isRecurring
						);

						if (isBookable) {
							hasBookableSlotForThisDay = true;
						}
					});
				});
			});

			// If no bookable slot found for this day, add it to unbookable dates
			if (!hasBookableSlotForThisDay) {
				if (!unbookableDates.some(d => timeutil.isSame(timeutil.getInstance(d), day, "day"))) {
					unbookableDates.push(day);
				}
			}
		});

		// Set error message if any selected date has no bookable slots
		if (unbookableDates.length > 0) {
			const duration = enrollmentRules.duration;
			const durationType = enrollmentRules.durationType;

			// Format dates: "17 Nov 25, 18 Nov 25 & 20 Nov 25"
			const formattedDates = unbookableDates.map(date => {
				const dateInstance = timeutil.getInstance(date);
				const day = dateInstance.format("D");
				const month = dateInstance.format("MMM");
				const year = dateInstance.format("YY");
				return `${day} ${month} ${year}`;
			});

			let dateList: string;
			if (formattedDates.length === 1) {
				dateList = formattedDates[0];
			} else if (formattedDates.length === 2) {
				dateList = `${formattedDates[0]} & ${formattedDates[1]}`;
			} else {
				// For 3+ dates: "17 Nov 25, 18 Nov 25 & 20 Nov 25"
				const allButLast = formattedDates.slice(0, -1).join(", ");
				const last = formattedDates[formattedDates.length - 1];
				dateList = `${allButLast} & ${last}`;
			}

			const durationText = duration === 0
				? "at least until the session starts"
				: `at least ${duration} ${durationType === 'hours' ? (duration === 1 ? 'hour' : 'hours') : (duration === 1 ? 'day' : 'days')} in advance`;

			setEnrollmentError({
				dateList,
				durationText
			});
		} else {
			setEnrollmentError(null);
		}
	}, [selectedDays, enrollmentRules, groupedTimings, selectedBatch, selectedWeekDays, selectedPricingPack]);

	/**
	 * Helper: Check if a slot is bookable for a specific date based on enrollment rules
	 */
	const isSlotBookableForDate = (
		slot: any,
		date: Date,
		dayOrDateStr: string,
		activityId: string,
		enrollmentRules: any,
		currentTime: number,
		isRecurring: boolean
	): boolean => {
		const match = Helper.findMatchingInterval(
			slot,
			dayOrDateStr,
			activityId,
			selectedBatch?.intervals || [],
			isRecurring,
			firebase
		);

		// If no matching interval found, consider it bookable (don't disable)
		if (!match) return true;

		// For specific batches, use the interval's date; for recurring, use the passed date
		const dateToUse = isRecurring ? date : (match.date ? timeutil.getInstance(match.date).toDate() : date);
		const slotStartTime = Helper.getSlotStartTimeForDate(match, dateToUse, isRecurring);
		return Helper.isSlotBookableByEnrollment(slotStartTime, enrollmentRules, currentTime, firebase);
	};

	/**
	 * Helper: Get day/date string identifier for a date
	 * - Recurring batches: returns day name (e.g., "monday")
	 * - Specific batches: returns formatted date (e.g., "12/11/2024")
	 */
	const getDayOrDateString = (date: Date, isRecurring: boolean): string => {
		return isRecurring
			? timeutil.inFormat(date, "dddd").toLowerCase()
			: timeutil.getInstance(date).format(Helper.dateFormat(firebase));
	};


	const getFilteredDaysList = (): Date[] => {
		// For WEEK (and monthly days-per-week) packs with selectedWeekDays, filter to only matching weekdays
		if (((selectedPricingPack?.pricingPack?.frequency === "WEEK" &&
			selectedPricingPack?.pricingPack?.all === false) || isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack)) &&
			selectedWeekDays?.length) {
			return selectedDays?.filter((day) => {
				const dayName = timeutil.inFormat(day, "dddd");
				return selectedWeekDays?.includes(dayName.toUpperCase());
			}) || [];
		}
		return selectedDays || [];
	};

	const getFilteredDays = (day) => {
		return selectedDays?.some((item) => {
			const dayName = timeutil.inFormat(item, "dddd");
			return day.toUpperCase() == dayName.toUpperCase();
		})
	}

	/**
	 * Helper: Find matching date from selectedDays for a day/date identifier
	 * - Recurring: matches by day name (e.g., "monday")
	 * - Specific: matches by formatted date (e.g., "12/11/2024")
	 */
	const findMatchingDate = (dayOrDateStr: string, isRecurring: boolean): Date | null => {
		if (isRecurring) {
			return selectedDays.find((day) => {
				const dayStr = timeutil.inFormat(day, "dddd").toLowerCase();
				return dayStr === dayOrDateStr.toLowerCase();
			}) || null;
		} else {
			return selectedDays.find((day) => {
				const dayStr = timeutil.getInstance(day).format(Helper.dateFormat(firebase));
				return dayStr === dayOrDateStr;
			}) || null;
		}
	};

	/**
	 * Helper: Normalize slot selection to array format
	 * Handles both string and array formats
	 */
	const normalizeSlotSelection = (slotSelection: string | string[] | undefined): string[] => {
		if (!slotSelection) return [];
		return Array.isArray(slotSelection) ? slotSelection : [slotSelection];
	};

	/**
	 * Helper: Filter "Common slots" - slots that must be bookable for ALL relevant selected days
	 * For WEEK frequency packs, only checks days matching selectedWeekDays
	 */
	const filterBookableCommonSlots = (
		commonSlots: string[],
		activityId: string,
		enrollmentRules: any,
		currentTime: number,
		isRecurring: boolean
	): string[] => {
		return commonSlots.filter((slotKey) => {
			const slot = groupedTimings[activityId]?.["Common slots"]?.find((s) => s.key === slotKey);
			if (!slot) return false;

			// Get filtered days list (for WEEK packs, only includes matching weekdays)
			const daysToCheck = getFilteredDaysList();
			if (daysToCheck.length === 0) return false;

			// Common slots must be bookable for ALL relevant selected days
			return daysToCheck.every((day) => {
				const dayOrDateStr = getDayOrDateString(day, isRecurring);
				return isSlotBookableForDate(
					slot,
					day,
					dayOrDateStr,
					activityId,
					enrollmentRules,
					currentTime,
					isRecurring
				);
			});
		});
	};

	/**
	 * Helper: Filter day-specific slots - slots tied to a specific day/date
	 */
	const filterBookableDaySpecificSlots = (
		daySlots: string[],
		dayOrDateStr: string,
		activityId: string,
		enrollmentRules: any,
		currentTime: number,
		isRecurring: boolean
	): string[] => {
		const matchingDate = findMatchingDate(dayOrDateStr, isRecurring);
		if (!matchingDate) return daySlots; // If date not selected, keep slots (they'll be filtered elsewhere)

		return daySlots.filter((slotKey) => {
			const slot = groupedTimings[activityId]?.[dayOrDateStr]?.find((s) => s.key === slotKey);
			if (!slot) return false;

			return isSlotBookableForDate(
				slot,
				matchingDate,
				dayOrDateStr,
				activityId,
				enrollmentRules,
				currentTime,
				isRecurring
			);
		});
	};

	/**
	 * Helper: Update timings object with filtered slots
	 * Returns true if any changes were made
	 */
	const updateTimingsForActivity = (
		updatedTimings: any,
		activityId: string,
		dayOrDate: string,
		bookableSlots: string[],
		originalSlots: string[]
	): boolean => {
		if (bookableSlots.length === originalSlots.length) return false;

		if (bookableSlots.length === 0) {
			delete updatedTimings[activityId][dayOrDate];
		} else {
			updatedTimings[activityId][dayOrDate] = bookableSlots;
		}
		return true;
	};


	const cleanupEmptyActivities = (timings: any): void => {
		Object.keys(timings).forEach((activityId) => {
			if (timings[activityId] && Object.keys(timings[activityId]).length === 0) {
				delete timings[activityId];
			}
		});
	};

	/**
	 * Effect: Automatically unselect slots that are not bookable for selected dates
	 * Runs when selectedDays or selectedTimings change
	 */
	useEffect(() => {
		// Early exit if enrollment rules are not enabled or no data to process
		// Enrollment rules are enabled only if both duration and durationType are present
		const isEnrollmentEnabled = enrollmentRules &&
			enrollmentRules.duration != null &&
			enrollmentRules.durationType &&
			enrollmentRules.duration >= 0;
		if (!isEnrollmentEnabled || !selectedDays?.length || !selectedTimings) {
			return;
		}

		const currentTime = timeutil.getEpoch(timeutil.getInstance());
		const isRecurring = selectedBatch?.type === "recurring";
		let shouldUpdateTimings = false;
		const updatedTimings = JSON.parse(JSON.stringify(selectedTimings));

		// Process each activity's slot selections
		Object.entries(selectedTimings).forEach(([activityId, activitySelection]) => {
			if (!activitySelection) return;

			// Process "Common slots" - slots that apply to all selected days
			if (activitySelection["Common slots"]) {
				const commonSlots = normalizeSlotSelection(activitySelection["Common slots"]);
				const bookableSlots = filterBookableCommonSlots(
					commonSlots,
					activityId,
					enrollmentRules,
					currentTime,
					isRecurring
				);

				if (updateTimingsForActivity(updatedTimings, activityId, "Common slots", bookableSlots, commonSlots)) {
					shouldUpdateTimings = true;
				}
			}

			// Process day-specific slots - slots tied to a specific day/date
			Object.keys(activitySelection).forEach((dayOrDate) => {
				if (dayOrDate === "Common slots") return;

				const daySlots = normalizeSlotSelection(activitySelection[dayOrDate.toUpperCase()]);
				const bookableSlots = filterBookableDaySpecificSlots(
					daySlots,
					dayOrDate,
					activityId,
					enrollmentRules,
					currentTime,
					isRecurring
				);

				if (updateTimingsForActivity(updatedTimings, activityId, dayOrDate, bookableSlots, daySlots)) {
					shouldUpdateTimings = true;
				}
			});
		});

		// Clean up empty activity objects
		cleanupEmptyActivities(updatedTimings);

		// Update selected timings if any changes were made
		if (shouldUpdateTimings) {
			setSelectedTimings(updatedTimings);
		}
	}, [selectedDays, enrollmentRules, selectedBatch, groupedTimings, firebase, selectedTimings]);
	const formatTime = (epoch: number) => timeutil.getInstance(epoch).format("h:mm A");


	const getAvailabilityForSelectedSlots = () => {
		const maxCapacity = maxCapacityPerBatchCenter || 0;
		let availability = maxCapacity;
		if (selectSlotsLater) return maxCapacity
		const isRecurring = selectedBatch.type === "recurring";
		Object.entries(selectedTimings).forEach(
			([actId, activitySelection]) => {
				if (!activitySelection["Common slots"]) return;
				let slotObj = groupedTimings[actId]["Common slots"].find((ele) => ele.key == activitySelection["Common slots"] || activitySelection["Common slots"].includes(ele.key));
				const timeLabel = slotObj?.label;
				selectedDays?.forEach(day => {
					let dayOrDateStr = isRecurring ? timeutil.inFormat(day, "dddd").toLowerCase() : timeutil
						.getInstance(day)
						.format(Helper.dateFormat(firebase));
					const match = selectedBatch.intervals.find((obj: any) => {
						const objTime = `${formatTime(obj.startTime)} - ${formatTime(obj.endTime)}`;
						const objDayOrDate = isRecurring
							? obj.day.toLowerCase()
							: timeutil
								.getInstance(obj.date)
								.format(Helper.dateFormat(firebase));
						return objDayOrDate === dayOrDateStr && objTime === timeLabel && (actId === "default" ? !obj.activityId : obj.activityId === actId);
					});
					if (!match) return;
					const slotKey = `${selectedBatch.id}-${match?.randomId}-${timeutil.getEpoch(day)}-${centerId}`;
					if (batchSlotMap && (batchSlotMap[slotKey] || batchSlotMap[slotKey] == 0)) {
						availability = Math.min(availability, batchSlotMap[slotKey]);
					}
				});
			})
		setCommonAvailability(availability);
	}


	const getAvailabilityPerSlot = (slot, activityId) => {
		if (!selectedDays?.length) return { text: "", color: "" };
		const maxCapacity = maxCapacityPerBatchCenter || 0;
		let availability = maxCapacity;
		const isRecurring = selectedBatch.type === "recurring";
		const timeLabel = slot?.label;
		const actId = activityId;
		let daysList = (selectedPricingPack?.pricingPack?.frequency == "WEEK" && selectedPricingPack?.pricingPack?.all == false) || isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack) ?
			selectedDays?.filter((day) => {
				let dayName = timeutil.inFormat(day, "dddd");
				return selectedWeekDays?.includes(dayName.toUpperCase());
			})
			: [...selectedDays];

		daysList.forEach(day => {
			let dayOrDateStr = isRecurring ? timeutil.inFormat(day, "dddd").toLowerCase() : timeutil
				.getInstance(day)
				.format(Helper.dateFormat(firebase));
			const match = selectedBatch.intervals.find((obj: any) => {
				const objTime = `${formatTime(obj.startTime)} - ${formatTime(obj.endTime)}`;
				const objDayOrDate = isRecurring
					? obj.day.toLowerCase()
					: timeutil
						.getInstance(obj.date)
						.format(Helper.dateFormat(firebase));
				return objDayOrDate === dayOrDateStr && objTime === timeLabel && (actId === "default" ? !obj.activityId : obj.activityId === actId);
			});
			const slotKey = `${selectedBatch.id}-${match?.randomId}-${timeutil.getEpoch(day)}-${centerId}`;
			if (batchSlotMap && (batchSlotMap[slotKey] || batchSlotMap[slotKey] == 0)) {
				availability = Math.min(availability, batchSlotMap[slotKey]);
			}
		});

		if (availability === maxCapacity) {
			return { text: "Available", color: "#00855A" };
		}

		const current = availability || 0;

		// Calculate percentage
		const percentage = maxCapacity > 0 ? (current / maxCapacity) * 100 : 0;
		// Determine status
		if (current <= 0) {
			if (allowWaitlist && !waitlistCapacity) {
				return {
					text: "Slots filled - waitlist",
					color: "#E56C32"
				};
			} else if (allowWaitlist && waitlistCapacity) {
				if (current + waitlistCapacity <= 0) {
					return {
						text: "Sold out",
						color: "#8D8D8D"
					};
				} else {
					return {
						text: "Slots filled - waitlist",
						color: "#E56C32"
					};
				}
			}
			return {
				text: "Sold out",
				color: "#8D8D8D"
			};
		} else if (percentage >= 50) {
			return { text: "Available", color: "#00855A" };
		} else if (percentage >= 20) {
			return { text: "Filling fast", color: "#E09D00" };
		} else {
			return { text: `${current} slots left`, color: "#FD5F85" };
		}
	};


	// Helper function to count the number of classes (timings) selected for a window
	// Includes both currently selected slots and previously selected slots (for selectSlotsLater case)
	const countClassesInWindow = (window: { start: Date; end: Date; days: Date[] }) => {
		if (!selectedTimings || !window.days.length) {
			// If no current selections, count only previously selected slots
			if (previouslySelectedSlots.length === 0) return 0;
		}

		const isRecurring = selectedBatch.type === "recurring";
		let totalClasses = 0;
		// Count currently selected slots
		if (selectedTimings && window.days.length) {
			window.days.forEach(day => {
				// Convert day to the format used in selectedTimings
				const dayOrDateStr = isRecurring
					? timeutil.inFormat(day, "dddd").toLowerCase()
					: timeutil.getInstance(day).format(Helper.dateFormat(firebase));

				// Count timings across all activities for this day
				Object.values(selectedTimings).forEach((activitySelection: any) => {
					if (!activitySelection) return;

					// Check for "Common slots" - applies to all days
					const commonSlots = activitySelection["Common slots"];
					if (commonSlots) {
						if (Array.isArray(commonSlots)) {
							totalClasses += commonSlots.length;
						} else if (typeof commonSlots === "string") {
							totalClasses += 1;
						}
					}

					// Check for day-specific timings
					const timingsForDay = activitySelection[dayOrDateStr.toUpperCase()];
					if (timingsForDay) {
						// If it's an array, count its length; if it's a string, count as 1
						if (Array.isArray(timingsForDay)) {
							totalClasses += timingsForDay.length;
						} else if (typeof timingsForDay === "string") {
							totalClasses += 1;
						}
					}
				});
			});
		}

		// Count previously selected slots that fall within this window
		if (previouslySelectedSlots.length > 0) {
			const windowStartEpoch = timeutil.getEpoch(timeutil.getInstance(window.start).startOf("day"));
			const windowEndEpoch = timeutil.getEpoch(timeutil.getInstance(window.end).endOf("day"));

			previouslySelectedSlots.forEach((slot: any) => {
				const slotDateEpoch = slot.date;
				if (slotDateEpoch >= windowStartEpoch && slotDateEpoch <= windowEndEpoch) {
					totalClasses += 1;
				}
			});
		}

		return totalClasses;
	};

	// Helper function to calculate windows from selected days
	// Windows are created from the first selected day forward based on duration
	const calculateWindowsFromDays = (days: Date[]) => {
		if (!selectedPricingPack?.pricingPack?.durationValue || !selectedPricingPack?.pricingPack?.durationUnit) {
			return [];
		}

		const sortedDays = [...days].sort((a, b) => a.getTime() - b.getTime());
		const durationValue = selectedPricingPack.pricingPack.durationValue;
		const durationUnit = selectedPricingPack.pricingPack.durationUnit.toLowerCase();
		const windows: Array<{ start: Date; end: Date; days: Date[] }> = [];

		for (const day of sortedDays) {
			const dayInstance = timeutil.getInstance(day);
			let addedToWindow = false;

			// Check if this day falls within any existing window
			for (const window of windows) {
				const windowStart = timeutil.getInstance(window.start);
				const windowEnd = timeutil.getInstance(window.end);

				if ((timeutil.isSame(dayInstance, windowStart, "day") || timeutil.isAfter(dayInstance, windowStart, "day")) &&
					(timeutil.isSame(dayInstance, windowEnd, "day") || timeutil.isBefore(dayInstance, windowEnd, "day"))) {
					window.days.push(day);
					addedToWindow = true;
					break;
				}
			}

			// If not added to any existing window, create a new window. We do NOT
			// skip when windows.length >= credits — callers (computedDisabled,
			// onSelect, the clamp useEffect) need to *see* the over-credits state
			// to reject it. Skipping here silently masked violations and let
			// invalid picks through.
			if (!addedToWindow) {
				let windowStart = getInstanceClone(dayInstance).startOf("day");
				let windowEnd = getInstanceClone(windowStart);

				if (durationUnit === "months") {
					// For months: Nov 3 + 3 months = Feb 3 (inclusive, same day 3 months later)
					windowEnd = windowEnd.add(durationValue, "month").endOf("day");
				} else if (durationUnit === "weeks") {
					// Week-aligned window: snap start to start of week, end at end of (start + N-1) weeks.
					const ws = selectedBatch?.weekStartDay;
					const weekWindow = timeutil.consecutiveWeeksWindow(windowStart, durationValue, ws);
					windowStart = weekWindow.start;
					windowEnd = weekWindow.end;
				} else if (durationUnit === "days") {
					// For days: Nov 3 + 3 days = Nov 6 (inclusive, 3 days from Nov 3)
					windowEnd = windowEnd.add(durationValue - 1, "day").endOf("day");
				}

				// If batch ends before window, use batch's endDate
				if (selectedBatch?.endDate) {
					const batchEndDate = timeutil.getInstance(selectedBatch.endDate).endOf("day");
					if (timeutil.isAfter(windowEnd, batchEndDate)) {
						// Window extends beyond batch end, use batch endDate
						windowEnd = batchEndDate;
					}
				}

				windows.push({
					start: Helper.convertToDate(windowStart, firebase),
					end: Helper.convertToDate(windowEnd, firebase),
					days: [day]
				});
			}
		}

		return windows;
	};

	// The windows the session cap counts over. Only the cap uses these: how many windows a
	// booking may span (credits) is still measured in "Pack runs for" windows.
	const capWindows = (days: Date[]) => limitWindows(days, selectedPricingPack?.pricingPack, {
		periodCounting, anchor: limitAnchor, legacy: calculateWindowsFromDays,
	});

	// For CUSTOM + DURATION + WEEKS: a week is "claimed" if either it appears in
	// selectedWeeks (set via Week-label click) or it contains a date from
	// selectedDays. The whole row of a claimed week renders with a light-purple
	// background; selected dates inside that row remain solid purple.
	const ws = selectedBatch?.weekStartDay;
	const batchWeekRange = useMemo(() => {
		const startSource = selectedBatch?.startDate
			? timeutil.getInstance(selectedBatch.startDate)
			: (selectedBatch?.intervals?.length
				? timeutil.getInstance(Math.min(...selectedBatch.intervals.map(d => timeutil.getEpoch(timeutil.getInstance(d.date)))))
				: null);
		const endSource = selectedBatch?.endDate
			? timeutil.getInstance(selectedBatch.endDate)
			: (selectedBatch?.intervals?.length
				? timeutil.getInstance(Math.max(...selectedBatch.intervals.map(d => timeutil.getEpoch(timeutil.getInstance(d.date)))))
				: null);
		if (!startSource) return { start: null, end: null };
		return {
			start: timeutil.getEpoch(timeutil.getStartOfWeek(startSource, ws)),
			end: endSource ? timeutil.getEpoch(timeutil.getStartOfWeek(endSource, ws)) : null,
		};
	}, [selectedBatch?.startDate, selectedBatch?.endDate, selectedBatch?.intervals, ws]);

	const batchStartWeekEpoch = batchWeekRange.start;

	const weekNumberFromBatchStart = (date: Date | any): number | null => {
		if (batchStartWeekEpoch == null) return null;
		const thisWeekEpoch = timeutil.getEpoch(timeutil.getStartOfWeek(date, ws));
		if (thisWeekEpoch < batchStartWeekEpoch) return null;
		if (batchWeekRange.end != null && thisWeekEpoch > batchWeekRange.end) return null;
		return Math.round((thisWeekEpoch - batchStartWeekEpoch) / (7 * 24 * 60 * 60 * 1000)) + 1;
	};

	const claimedWeekKeySet = useMemo(() => {
		const set = new Set<number>();
		for (const d of selectedDays || []) set.add(timeutil.getStartOfWeek(d, ws).valueOf());
		for (const label of selectedWeeks || []) {
			const parts = label.split("+");
			if (parts.length === 2) set.add(Number(parts[1]));
		}
		return set;
	}, [selectedDays, selectedWeeks, ws]);

	// Only mark cells as "claimed-week" if they're inside a claimed week AND
	// otherwise eligible (skips weekends / out-of-range / past dates so the
	// row highlight doesn't bleed onto unbookable cells).
	const isWeekClaimed = (date: Date) => {
		if (!claimedWeekKeySet.has(timeutil.getStartOfWeek(date, ws).valueOf())) return false;
		if (disabled(date)) return false;
		return true;
	};

	const autoFillDatesForWeekKeys = (weekKeys: number[]): Date[] => {
		const newDays: Date[] = [];
		for (const wk of weekKeys) {
			for (let i = 0; i < 7; i++) {
				const candidate = getInstanceClone(timeutil.getInstance(wk)).add(i, "day");
				const candidateAsDate = Helper.convertToDate(candidate, firebase);
				if (!disabled(candidateAsDate)) {
					newDays.push(candidateAsDate);
				}
			}
		}
		return newDays.sort((a, b) => a.getTime() - b.getTime());
	};

	// Click a Week label: snap-select N consecutive weeks anchored at this week.
	// Re-clicking an already-claimed week clears the whole selection.
	// Unlimited mode: silently auto-fill all eligible dates in those N weeks so
	//                 the booking payload has slots; visually those dates blend
	//                 with the row (no extra "selected" highlight) and individual
	//                 day clicks are blocked (see computedDisabled).
	// Limited mode: do not auto-fill — user picks individual dates within the
	//               claimed weeks; the noOfClasses cap is enforced.
	const selectConsecutiveWeeksAnchoredAt = (anchorDate: Date) => {
		const pricingPack = selectedPricingPack?.pricingPack;
		if (!pricingPack || pricingPack.frequency !== "CUSTOM" || pricingPack.durationType !== "DURATION") return;
		if ((pricingPack.durationUnit || "").toLowerCase() !== "weeks") return;
		const durationValue = pricingPack.durationValue;
		if (!durationValue || durationValue < 1) return;

		const isUnlimited = !!pricingPack.unlimitedClasses;
		const noOfClasses = pricingPack.noOfClasses;
		// Treat as "limited" whenever a session cap is set, even if unlimitedClasses
		// is also true (defends against admin-side misconfig).
		const isLimited = !isUnlimited || (noOfClasses && noOfClasses > 0);
		const shouldAutoFill = !isLimited;

		const weekStart = timeutil.getStartOfWeek(anchorDate, ws);
		const weekStartMs = weekStart.valueOf();

		const weekHasEligibleDate = (wkKey: number) => {
			for (let i = 0; i < 7; i++) {
				const candidate = getInstanceClone(timeutil.getInstance(wkKey)).add(i, "day");
				const candidateAsDate = Helper.convertToDate(candidate, firebase);
				if (!disabled(candidateAsDate)) return true;
			}
			return false;
		};
		const buildKeys = (offset: number) => {
			const keys: number[] = [];
			for (let i = 0; i < durationValue; i++) {
				keys.push(getInstanceClone(weekStart).add(i - offset, "week").valueOf());
			}
			return keys;
		};

		// Default: snap forward (clicked week = first of N). If that runs past the
		// valid range, slide the window backward so the clicked week sits later in
		// it — pick the smallest shift that keeps every week in range. Falls back
		// to the forward snap if no shift fits.
		let targetWeekKeys = buildKeys(0);
		if (!targetWeekKeys.every(weekHasEligibleDate)) {
			for (let offset = 1; offset < durationValue; offset++) {
				const candidateKeys = buildKeys(offset);
				if (candidateKeys.every(weekHasEligibleDate)) {
					targetWeekKeys = candidateKeys;
					break;
				}
			}
		}
		const targetWeekKeySet = new Set(targetWeekKeys);
		const targetWeekLabels = targetWeekKeys.map(k => `Week+${k}`);

		// Re-click on a claimed week → clear everything.
		if (claimedWeekKeySet.has(weekStartMs)) {
			setSelectedWeeks([]);
			setSelectedDays([]);
			setSelectedTimings({});
			return;
		}

		// Otherwise replace selection with the new N-week snap.
		setSelectedWeeks(targetWeekLabels);

		if (shouldAutoFill) {
			setSelectedDays(autoFillDatesForWeekKeys(targetWeekKeys));
		} else {
			// Limited: prefill the first `noOfClasses` eligible dates from the
			// snapped weeks so the user sees dark-purple "selected" squares right
			// away. They can click individual dates to swap their picks.
			const cap = (noOfClasses && noOfClasses > 0) ? noOfClasses : 0;
			if (cap > 0) {
				const allEligible = autoFillDatesForWeekKeys(targetWeekKeys);
				setSelectedDays(allEligible.slice(0, cap));
			} else {
				setSelectedDays(prev => prev.filter(d => {
					const dWeekKey = timeutil.getStartOfWeek(d, ws).valueOf();
					return targetWeekKeySet.has(dWeekKey);
				}).sort((a, b) => a.getTime() - b.getTime()));
			}
		}
	};

	// For CUSTOM with DURATION type, we allow direct day selection
	// Windows are created automatically from the first selected day forward
	const computedDisabled = (date: Date) => {
		const isOriginallyDisabled = disabled(date);
		if (isOriginallyDisabled) return true;

		// For CUSTOM with DURATION type, only restrict by weekdays if selected
		if (selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
			selectedPricingPack?.pricingPack?.durationType === "DURATION") {
			// If weekdays are selected, only allow days matching those weekdays
			if (selectedWeekDays.length > 0) {
				const dayName = timeutil.inFormat(date, "dddd").toUpperCase();
				if (!selectedWeekDays.includes(dayName)) return true;
			}

			const durationUnit = selectedPricingPack?.pricingPack?.durationUnit?.toLowerCase();
			const noOfClasses = selectedPricingPack?.pricingPack?.noOfClasses;
			const isUnlimited = selectedPricingPack?.pricingPack?.unlimitedClasses;

			// For WEEKS: simulate adding `date` and reject if it creates a non-consecutive
			// or oversized claim (window snaps to whole weeks; picks must stay within
			// `durationValue` consecutive weeks per window).
			if (durationUnit === "weeks" && selectedDays.length > 0) {
				const effectiveCredits = (credits && credits > 0) ? credits : 1;
				const alreadySelected = selectedDays.some((d) => timeutil.isSame(timeutil.getInstance(d), date, "day"));
				if (alreadySelected) return false;

				const simulatedDays = [...selectedDays, date];
				const simulatedWindows = calculateWindowsFromDays(simulatedDays);

				if (simulatedWindows.length > effectiveCredits) return true;

				// Enforce the per-window session cap whenever noOfClasses is set.
				// Cap on days.length first — countClassesInWindow looks at
				// selectedTimings and stays at 0 until the user picks any timing,
				// which let too many date picks slip through.
				if (noOfClasses && noOfClasses > 0) {
					for (const window of capWindows(simulatedDays)) {
						if (window.days.length > noOfClasses) return true;
						if (countClassesInWindow(window) > noOfClasses) return true;
					}
				}
				return false;
			}

			// Check if credits (number of windows) limit is reached
			// If selecting this date would create a new window beyond the limit, disable it
			if (credits && credits > 0 && selectedDays.length > 0) {
				const existingWindows = calculateWindowsFromDays(selectedDays);

				// If we've already reached the credits limit, check if this date would be in an existing window
				if (existingWindows.length >= credits) {
					const dateInstance = timeutil.getInstance(date);
					let wouldBeInExistingWindow = false;

					// Check if this date would fall within any existing window
					for (const window of existingWindows) {
						const windowStart = timeutil.getInstance(window.start);
						const windowEnd = timeutil.getInstance(window.end);

						if ((timeutil.isSame(dateInstance, windowStart, "day") || timeutil.isAfter(dateInstance, windowStart, "day")) &&
							(timeutil.isSame(dateInstance, windowEnd, "day") || timeutil.isBefore(dateInstance, windowEnd, "day"))) {
								wouldBeInExistingWindow = true;
								break;
						}
					}

					// If it wouldn't be in an existing window and we've reached the limit, disable it
					if (!wouldBeInExistingWindow) {
						return true;
					}
				}
			}
		}

		return false;
	};
	return (
		<div>
			{selectSlotsLater ?
				!selectedPricingPack?.pricingPack || selectedPricingPack?.pricingPack?.frequency === "ENTIRE_PACKAGE_DURATION" || (selectedPricingPack?.pricingPack?.frequency === "CUSTOM") ?
					null : <div className='book-p-39 book-main-card'>
						<label className='global-required' >{formatMsg("enterNumberOf") + " " + formatMsg(Helper.getSubFrequency(selectedPricingPack?.pricingPack?.frequency))}</label>
						<br />
						<InputNumber
							style={{ marginTop: 12, width: "100%" }}
							min={0}
							max={maxFrequency[selectedPricingPack?.pricingPack?.frequency] ?? undefined}
							value={credits}
							onChange={e => !readOnly && setCredits(e)}
							className="book-input-number"
							placeholder={formatMsg("enterNumberOf") + " " + formatMsg(Helper.getSubFrequency(selectedPricingPack?.pricingPack?.frequency))}
						/>
					</div> :
				centerId &&
					(selectedPricingPack?.pricingPack?.frequency !== "ENTIRE_PACKAGE_DURATION" || Object.values(groupedTimings).some(item =>
						item && Object.values(item).some(inner => Array.isArray(inner) && inner.length > 0)
					) || viewAll) ?
					<Row className='book-schedule-container book-schedule-container-full' justify={hideTimings ? "center" : undefined}>
						{(selectedPricingPack?.pricingPack?.frequency !== "ENTIRE_PACKAGE_DURATION" || viewAll) ?
							<div className='book-calendar-container book-calendar-container-full'>
								{/* Display selected windows for CUSTOM with DURATION - ABOVE calendar.
								    Hidden for durationUnit=WEEKS (window is visible directly in the calendar
								    via the Week N column); kept for DAYS/MONTHS until those are reworked. */}
								{!hideDays && selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
									selectedPricingPack?.pricingPack?.durationType === "DURATION" &&
									(selectedPricingPack?.pricingPack?.durationUnit || "").toLowerCase() !== "weeks" &&
									selectedDays?.length > 0 &&
									selectedPricingPack?.pricingPack?.durationValue &&
									selectedPricingPack?.pricingPack?.durationUnit ? (
									<div className='book-main-card book-main-card-windows'>
										<div className={`book-windows-container ${viewAll ? 'book-windows-container-center' : 'book-windows-container-start'}`}>
											{calculateWindowsFromDays(selectedDays).map((window, index) => {
												const windowStart = timeutil.getInstance(window.start);
												const windowEnd = timeutil.getInstance(window.end);
												const noOfClasses = selectedPricingPack.pricingPack.noOfClasses;
												const isUnlimited = selectedPricingPack.pricingPack.unlimitedClasses;
												const selectedClasses = countClassesInWindow(window);
												const progressPercentage = isUnlimited ? 0 : noOfClasses ? (selectedClasses / noOfClasses) * 100 : 0;
												const themeColor = Helper.isAccount(firebase) || !firebase.user ? firebase?.schoolConfig?.themeColor || colors.primaryBgColor || "#7413BD" : "#7413BD";
												return (
													<div
														key={index}
														className="book-window-card"
													>
														{/* First Row: Badge, Window Title, Delete Icon */}
														<div className="book-window-header">
															{/* Window Number Badge */}
															<div
																className="book-window-badge"
																style={{ background: themeColor }}
															>
																<span className="book-window-badge-text">
																	BP{index + 1}
																</span>
															</div>

															{/* Window Title */}
															<div className="book-window-title-container">
																<div className="book-window-title">
																	{formatMsg("bookingPeriod")} {index + 1}
																</div>
															</div>

															{/* Delete Icon */}
															{/* {!readOnly && (
																<div
																	className="book-window-delete-icon"
																	onClick={() => {
																		const windowDays = window.days;
																		setSelectedDays(prev => prev.filter(day =>
																			!windowDays.some(wd => timeutil.isSame(timeutil.getInstance(day), timeutil.getInstance(wd), "day"))
																		));
																	}}
																>
																	<DeleteOutlined />
																</div>
															)} */}
														</div>

														{/* Second Row: Date Range and Classes Progress */}
														<div>
															{/* Date Range */}
															<div className="book-window-section">
																<div className="book-window-section-header">
																	<CalendarOutlined className="book-window-section-icon" style={{ color: themeColor }} />
																	<span className="book-window-section-label">
																		{formatMsg("capitalized.dataRange")}
																	</span>
																</div>
																<div className="book-window-section-value">
																	{windowStart.format("MMM DD, YYYY")} - {windowEnd.format("MMM DD, YYYY")}
																</div>
															</div>

															{/* Classes Progress */}
															<div>
																<div className="book-window-section-header">
																	<ClockCircleOutlined className="book-window-section-icon" style={{ color: themeColor }} />
																	<span className="book-window-section-label">
																		{formatMsg("classesProgress")}
																	</span>
																</div>
																<div className="book-window-progress-container">
																	<div className="book-window-progress-bar">
																		<div
																			className="book-window-progress-fill"
																			style={{
																				width: `${Math.min(progressPercentage, 100)}%`,
																				background: themeColor
																			}}
																		/>
																	</div>
																	<span className="book-window-progress-text">
																		{selectedClasses}{!isUnlimited && noOfClasses ? `/${noOfClasses}` : ""}
																	</span>
																</div>
															</div>
														</div>
													</div>
												);
											})}
										</div>
									</div>
								) : null}
							</div>
							: null
						}
						{/* Calendar Card with Calendar and Timings side by side */}
						<div className='book-main-card book-main-card-calendar'>
							{enrollmentError && (
								<div style={{
									color: '#ff4d4f',
									backgroundColor: '#fff2f0',
									border: '1px solid #ffccc7',
									borderRadius: '4px',
									padding: '12px 16px',
									margin: '16px 0px',
									fontSize: '14px',
									lineHeight: '1.5',
									width: '100%'
								}}>
									{formatMsg("error.noSlotsAvailable")} <span style={{ fontWeight: 'bold' }}>{enrollmentError.dateList}</span>. {formatMsg("error.bookingsMustBeMade")} <span style={{ fontWeight: 'bold' }}>{enrollmentError.durationText}</span>.
								</div>
							)}
							<div className="book-calendar-timings-wrapper">
								{/* Calendar Section */}
								{!hideDays && selectedPricingPack?.pricingPack?.frequency === "ENTIRE_PACKAGE_DURATION" && selectedDays?.length > 0 && viewAll && (
									<div style={{ padding: '8px 0' }}>
										<div className="slots">
											<DayPicker
												mode="multiple"
												selected={selectedDays.map((d: any) => Helper.convertToDate(timeutil.getInstance(d), firebase))}
												weekStartsOn={timeutil.getWeekStartDow(selectedBatch?.weekStartDay)}
												disabled={() => true}
												modifiers={{
													booked: selectedDays.map((d: any) => Helper.convertToDate(timeutil.getInstance(d), firebase)),
												}}
												modifiersClassNames={{
													booked: "book-selected-day",
												}}
												startMonth={selectedBatch?.startDate ? Helper.convertToDate(timeutil.getInstance(selectedBatch?.startDate), firebase) : undefined}
												endMonth={selectedBatch?.endDate ? Helper.convertToDate(timeutil.getInstance(selectedBatch?.endDate), firebase) : undefined}
												styles={{ day_button: { cursor: "default" } }}
											/>
										</div>
									</div>
								)}
								{!hideDays && selectedPricingPack?.pricingPack?.frequency !== "ENTIRE_PACKAGE_DURATION" && <div>
									{!readOnly && <div className='book-package-name book-header-margin-bottom book-header-center'>{readOnly ? formatMsg("yourDates") : formatMsg('pickYourDates')}</div>}
									<div className="b-slots-container">
										{(selectedPricingPack?.pricingPack?.frequency === "MONTH" && !viewAll) ?
											<div className='slots book-slots-no-padding'>
												<div className="month-grid">
													<div className="book-month-label">{readOnly ? formatMsg("selectedMonths") : formatMsg("selectMonths")}</div>
													<div className="book-months-grid">
														{allMonths.map((month) => {
															const isSelected = selectedMonths.some((m) => timeutil.isSame(m, month, "month"));
															const isBeforeCurr = timeutil.isBefore(month, timeutil.getInstance(), "month");

															// Calculate which months will be selected for hover tooltip
															const isCustomDurationMonths = selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
																selectedPricingPack?.pricingPack?.durationType === "DURATION" &&
																selectedPricingPack?.pricingPack?.durationUnit?.toLowerCase() === "months" &&
																selectedPricingPack?.pricingPack?.durationValue;

															let hoverTooltip = "";
															if (isCustomDurationMonths && !isBeforeCurr && !readOnly) {
																const durationValue = selectedPricingPack.pricingPack.durationValue;
																const monthsToSelect = [];
																for (let i = 0; i < durationValue; i++) {
																	const monthToAdd = getInstanceClone(month).startOf("month").add(i, "month");
																	// Only add if the month exists in allMonths (within allowed range)
																	if (allMonths.some((m) => timeutil.isSame(m, monthToAdd, "month"))) {
																		monthsToSelect.push(monthToAdd);
																	}
																}
																if (monthsToSelect.length > 0) {
																	hoverTooltip = monthsToSelect.map(m => m.format("MMM YYYY")).join(", ");
																}
															}

															return (
																<div
																	key={month.format("YYYY-MM")}
																	onClick={() => {
																		if (readOnly || isBeforeCurr) return
																		toggleMonthSelection(month)
																	}}
																	title={hoverTooltip || undefined}
																	className="book-month-button"
																	style={{
																		borderColor: isBeforeCurr ? colors.color_lightGrey : colors.primaryBgColor,
																		cursor: isBeforeCurr ? "no-drop" : "pointer",
																		background: isSelected ? "var(--primary-color)" : "#fff",
																		color: isBeforeCurr ? colors.color_lightGrey : isSelected ? "#fff" : "var(--primary-color)",
																	}}
																>
																	{month.format("MMM YYYY")}
																</div>
															);
														})}
													</div>
												</div>
											</div> : null
										}
										<div className="slots">
											{selectedPricingPack?.pricingPack?.frequency === "ENTIRE_PACKAGE_DURATION" ||
												selectedPricingPack?.pricingPack?.frequency === "DAILY" ||
												(selectedPricingPack?.pricingPack?.frequency === "MONTH" && (selectedPricingPack?.pricingPack?.all || selectedPricingPack?.pricingPack?.frequencyType !== "WEEKS")) ||
												(selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
													selectedPricingPack?.pricingPack?.durationType === "DURATION") ?
												null :
												<div className="calendar-weeks">
													{weeksInMonth.map((week) => (
														<div
															key={week?.label}
															className={`week-box ${selectedWeeks.includes(week?.label) ? "selected" : ""} ${week.dates.every(w => disabled(w)) ? "week-disabled" : ""}`}
															onClick={() => !readOnly && toggleWeekSelection(week?.label, week?.dates)}
															style={{ cursor: readOnly ? "text" : undefined }}
														>
															<p className="book-week-text">
																{(() => {
																	const n = weekNumberFromBatchStart(week.dates[0]);
																	return n != null && n > 0 ? `Week ${n}` : "";
																})()}
															</p>
														</div>
													))}
												</div>
											}
											{(() => {
												const isCustomWeeksPack =
													selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
													selectedPricingPack?.pricingPack?.durationType === "DURATION" &&
													(selectedPricingPack?.pricingPack?.durationUnit || "").toLowerCase() === "weeks" &&
													!!selectedPricingPack?.pricingPack?.durationValue;
												const customWeeksComponents = isCustomWeeksPack ? {
													WeekNumber: (props: any) => {
														const week = props?.week;
														const firstDay = week?.days?.[0]?.date;
														if (!firstDay) return <td />;
														const weekStartEpoch = timeutil.getEpoch(timeutil.getStartOfWeek(firstDay, ws));
														const relativeWeekNum = weekNumberFromBatchStart(firstDay);
														const isClaimed = claimedWeekKeySet.has(weekStartEpoch);
														const allWeekDisabled = week.days.every((cd: any) => disabled(cd.date));
														const isClickable = !readOnly && !allWeekDisabled;
														return (
															<td style={{ padding: "0 8px", verticalAlign: "middle", background: isClaimed ? "rgba(116, 19, 189, 0.12)" : "transparent" }}>
																<span
																	onClick={() => isClickable && selectConsecutiveWeeksAnchoredAt(firstDay)}
																	style={{
																		cursor: isClickable ? "pointer" : "not-allowed",
																		color: allWeekDisabled ? colors.color_lightGrey : "var(--primary-color)",
																		borderRadius: 4,
																		padding: "4px 8px",
																		textDecoration: isClickable && !isClaimed ? "underline" : "none",
																		fontWeight: isClaimed ? 600 : 500,
																		fontSize: 13,
																		userSelect: "none",
																		whiteSpace: "nowrap",
																	}}
																>
																	{relativeWeekNum != null && relativeWeekNum > 0 ? `Week ${relativeWeekNum}` : ""}
																</span>
															</td>
														);
													},
													WeekNumberHeader: () => <th />,
												} : undefined;
												return (selectedPricingPack?.pricingPack?.frequency === "MONTH" && selectedPricingPack?.pricingPack?.all && !viewAll) ? null :
												<DayPicker
													timeZone={firebase.schoolConfig.timezone}
													showWeekNumber={isCustomWeeksPack}
													components={customWeeksComponents}
													mode={selectedPricingPack?.pricingPack?.frequency === "DAILY" || selectedPricingPack?.pricingPack?.frequencyType === "DAYS" || selectedPricingPack?.pricingPack?.frequency === "CUSTOM" ? "multiple" : undefined}
													onSelect={(days) => {
														if (readOnly) return
														// Monthly days-per-week: dates come from the selected months x weekdays, not clicks.
														if (isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack)) return
														// Unlimited + WEEKS: a day click is treated as a week claim for the
														// week containing that date — don't toggle individual sessions.
														const ppk = selectedPricingPack?.pricingPack;
														const isUnlimitedWeeksPack = ppk?.frequency === "CUSTOM"
															&& ppk?.durationType === "DURATION"
															&& (ppk?.durationUnit || "").toLowerCase() === "weeks"
															&& !!ppk?.unlimitedClasses
															&& (!ppk?.noOfClasses || ppk.noOfClasses <= 0);
														if (isUnlimitedWeeksPack) {
															const prevSet = new Set(selectedDays.map(d => d.getTime()));
															const newSet = new Set(days.map(d => d.getTime()));
															const newlyAdded = days.find(d => !prevSet.has(d.getTime()));
															const newlyRemoved = selectedDays.find(d => !newSet.has(d.getTime()));
															const anchor = newlyAdded || newlyRemoved;
															if (anchor) selectConsecutiveWeeksAnchoredAt(anchor);
															return;
														}
														let exceedsLimit = false;
														if (selectedPricingPack?.pricingPack?.frequency === "MONTH" && selectedPricingPack?.pricingPack?.frequencyType === "DAYS" && selectedPricingPack?.pricingPack?.frequencyValue) {
															const monthWiseCount = days.reduce((acc, day) => {
																const monthKey = timeutil.getInstance(day).format("YYYY-MM");
																acc[monthKey] = (acc[monthKey] || 0) + 1;
																return acc;
															}, {} as Record<string, number>);
															exceedsLimit = Object.values(monthWiseCount).some(count => count > selectedPricingPack?.pricingPack?.frequencyValue);
														}
														if (selectedPricingPack?.pricingPack?.frequency === "CUSTOM") {
															if (selectedPricingPack?.pricingPack?.durationType === "DURATION") {
																// New logic: Pick days first, then create windows from the first selected day forward
																// Windows are created automatically and do not overlap
																if (selectedPricingPack?.pricingPack?.durationValue && selectedPricingPack?.pricingPack?.durationUnit) {
																	const sortedDays = [...days].sort((a, b) => a.getTime() - b.getTime());

																	if (sortedDays.length === 0) {
																		setSelectedDays([]);
																		return;
																	}

																	// Calculate windows from selected days
																	const windows = calculateWindowsFromDays(sortedDays);

																	// Check if credits (number of windows) limit is reached
																	if (credits && credits > 0 && windows.length > credits) {
																		exceedsLimit = true;
																	}

																	// Cap days per window first (catches over-pick before
																	// timings are even chosen), then per-timing count.
																	if (selectedPricingPack?.pricingPack?.noOfClasses && selectedPricingPack.pricingPack.noOfClasses > 0) {
																		for (const window of capWindows(sortedDays)) {
																			if (window.days.length > selectedPricingPack.pricingPack.noOfClasses) {
																				exceedsLimit = true;
																				break;
																			}
																			const classesCount = countClassesInWindow(window);
																			if (classesCount > selectedPricingPack.pricingPack.noOfClasses) {
																				exceedsLimit = true;
																				break;
																			}
																		}
																	}

																	if (!exceedsLimit) {
																		setSelectedDays(sortedDays);
																		return;
																	}
																} else {
																	// Fallback: just check total count if duration info is missing.
																	// An open-ended recurring pack under per-period counting still has its
																	// windows (day/week/month, billing period, whole pack), so it is
																	// capped per window instead of on the total.
																	const ppk2 = selectedPricingPack.pricingPack;
																	if (!ppk2.unlimitedClasses && ppk2.noOfClasses) {
																		exceedsLimit = (periodCounting && ppk2.paymentType === "RECURRING")
																			? capWindows(days).some((w) => w.days.length > ppk2.noOfClasses || countClassesInWindow(w) > ppk2.noOfClasses)
																			: days.length > ppk2.noOfClasses;
																	}
																}
															} else {
																// For DATES type or no durationType, use the original logic
																if (!selectedPricingPack.pricingPack.unlimitedClasses && selectedPricingPack?.pricingPack?.noOfClasses) {
																	exceedsLimit = days.length > selectedPricingPack?.pricingPack?.noOfClasses;
																}
															}
														}
														if (!exceedsLimit) {
															setSelectedDays(days.sort((a, b) => a.getTime() - b.getTime()));
														}
													}}
													numberOfMonths={1}
													pagedNavigation
													showOutsideDays
													selected={isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack) ? getFilteredDaysList() : selectedDays}
													month={Helper.convertToDate(currentMonth, firebase)}
													captionLayout="dropdown"
													onMonthChange={(month) => setCurrentMonth(timeutil.getInstance(month))}
													modifiers={{
														selected: isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack) ? getFilteredDaysList() : selectedDays,
														disabled: computedDisabled,
														...(isCustomWeeksPack ? { claimedWeek: isWeekClaimed } : {}),
													}}
													modifiersClassNames={
														isCustomWeeksPack ? (
															// Unlimited: selection is by week — the picked dates blend
															// into the row tint (no separate "selected" class).
															// Limited: picked dates render solid purple via book-cw-selected.
															!!selectedPricingPack?.pricingPack?.unlimitedClasses && (!selectedPricingPack?.pricingPack?.noOfClasses || selectedPricingPack?.pricingPack?.noOfClasses <= 0)
																? { claimedWeek: "book-cw-claimed", selected: "book-cw-claimed" }
																: { claimedWeek: "book-cw-claimed", selected: "book-cw-selected" }
														) : undefined
													}
													weekStartsOn={timeutil.getWeekStartDow(selectedBatch?.weekStartDay)}
													disabled={computedDisabled}
													startMonth={selectedBatch?.startDate ? Helper.convertToDate(timeutil.getInstance(selectedBatch?.startDate), firebase) : Helper.convertToDate(timeutil.getInstance(Math.min(...selectedBatch?.intervals.map(d => timeutil.getEpoch(timeutil.getInstance(d.date))))), firebase)}
													endMonth={selectedBatch?.endDate ? Helper.convertToDate(timeutil.getInstance(selectedBatch?.endDate), firebase) : Helper.convertToDate(timeutil.getInstance(Math.max(...selectedBatch?.intervals.map(d => timeutil.getEpoch(timeutil.getInstance(d.date))))), firebase)}
													styles={{
														day_button: {
															cursor: readOnly ? "text" : undefined,
															...(isCustomWeeksPack ? { textDecoration: "none" } : {}),
														},
														...(isCustomWeeksPack ? { day: { textDecoration: "none" } } : {}),
													}}
													animate
												/>;
											})()}
										</div>
										{(selectedPricingPack?.pricingPack?.frequency !== "ENTIRE_PACKAGE_DURATION" &&
											selectedPricingPack?.pricingPack?.frequency !== "DAILY" &&
											selectedPricingPack?.pricingPack?.frequency !== "MONTH" &&
											selectedPricingPack?.pricingPack?.frequency !== "CUSTOM" &&
											(selectedPricingPack?.pricingPack?.frequency === "WEEK" && selectedPricingPack?.pricingPack?.frequencyValue < 7)) &&
											!(selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
												selectedPricingPack?.pricingPack?.durationType === "DURATION") ||
											isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack) ?
											<div className='day-picker-div'>
												<div className='global-flex slots-weekdays'>
													{["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"].filter(w =>
														selectedBatch.type === "recurring" ? selectedBatch.intervals.some(interval => interval.day === w) : true
													).map(w => (
														<div
															key={w}
															className={`book-weekdays ${selectedWeekDays.includes(w) ? "selected" : ""}`}
															onClick={() => {
																if (readOnly) return
																setSelectedWeekDays(prev => {
																	if (prev.includes(w))
																		return prev.filter(week => week !== w)
																	else {
																		// For CUSTOM with weeks duration, don't restrict by frequencyValue
																		if (selectedPricingPack?.pricingPack?.frequency === "CUSTOM") {
																			return [...prev, w]
																		}
																		if (!selectedPricingPack?.pricingPack?.all && selectedPricingPack?.pricingPack?.frequencyValue < 7 && selectedWeekDays?.length === selectedPricingPack?.pricingPack?.frequencyValue) return [...prev]
																		return [...prev, w]
																	}
																})
															}}
															style={{ cursor: readOnly ? "text" : undefined }}
														>
															{w.toLowerCase().slice(0, 3)}
														</div>
													))}
												</div>
											</div>
											: null}
										{!readOnly && selectedPricingPack?.pricingPack?.frequency !== "ENTIRE_PACKAGE_DURATION" && (selectedDays.length || selectedWeekDays.length || selectedMonths.length || selectedWeeks.length) ?
											<div
												className='clear-dates'
												onClick={() => {
													if (readOnly) return
													setSelectedDays([])
													setSelectedWeekDays([])
													setSelectedMonths([])
													setSelectedWeeks([])
													setSelectedTimings({})
												}}
											>
												{formatMsg("clearDates")}
											</div> :
											null
										}
									</div>
								</div>}
								{/* Timings Section */}
								{!hideTimings && (() => {
									// For WEEK (and monthly days-per-week) packs, require selectedWeekDays before showing timings
									const isWeekPack = (selectedPricingPack?.pricingPack?.frequency === "WEEK" &&
										selectedPricingPack?.pricingPack?.all === false) || isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack);
									const shouldShowTimings = selectedDays?.length &&
										(!isWeekPack || selectedWeekDays?.length > 0);

									return shouldShowTimings;
								})() ?
									<div className='book-timing-container book-timing-container-flex'>
										{!readOnly && <div className='book-package-name book-header-margin-bottom book-header-center'>{formatMsg('timings')}</div>}
										<div className='global-flex book-timing-wrapper'>
											<div className="timing-container">
												{Object.entries(groupedTimings)?.map(([activityId, slotsByDay]: any) => {
													if (!slotsByDay) return;
													return (
														<div key={activityId} className={(firebase.schoolConfig.showPackageActivities) ? "book-activity-group" : ""}>
															{activityId !== 'default' && firebase.schoolConfig.showPackageActivities &&
																<div
																	className='book-activity-name book-activity-name-clickable'
																	onClick={() => setOpenActivityId(activityId)}
																>
																	<span>
																		{packageActivityMap.get(activityId)?.name || ""}
																	</span>
																	<svg className='booking-activity-name-icon' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																		<path d="M17.9239 11.6192C17.8763 11.4965 17.8049 11.3843 17.7139 11.2892L12.7139 6.28921C12.6207 6.19597 12.51 6.12201 12.3882 6.07155C12.2663 6.02109 12.1358 5.99512 12.0039 5.99512C11.7376 5.99512 11.4822 6.10091 11.2939 6.28921C11.2007 6.38245 11.1267 6.49314 11.0762 6.61496C11.0258 6.73678 10.9998 6.86735 10.9998 6.99921C10.9998 7.26551 11.1056 7.52091 11.2939 7.70921L14.5939 10.9992H7.00391C6.73869 10.9992 6.48434 11.1046 6.2968 11.2921C6.10926 11.4796 6.00391 11.734 6.00391 11.9992C6.00391 12.2644 6.10926 12.5188 6.2968 12.7063C6.48434 12.8939 6.73869 12.9992 7.00391 12.9992H14.5939L11.2939 16.2892C11.2002 16.3822 11.1258 16.4928 11.075 16.6146C11.0242 16.7365 10.9981 16.8672 10.9981 16.9992C10.9981 17.1312 11.0242 17.2619 11.075 17.3838C11.1258 17.5056 11.2002 17.6162 11.2939 17.7092C11.3869 17.8029 11.4975 17.8773 11.6193 17.9281C11.7412 17.9789 11.8719 18.005 12.0039 18.005C12.1359 18.005 12.2666 17.9789 12.3885 17.9281C12.5103 17.8773 12.6209 17.8029 12.7139 17.7092L17.7139 12.7092C17.8049 12.6141 17.8763 12.502 17.9239 12.3792C18.0239 12.1357 18.0239 11.8627 17.9239 11.6192Z" fill={firebase.schoolConfig.themeColor || "#7413BD"} />
																	</svg>
																</div>
															}

															{slotsByDay && Object.entries(slotsByDay).map(
																([dayOrDate, timings]: any) => {
																	if (dayOrDate && dayOrDate !== 'Common slots' && !getFilteredDays(dayOrDate)) return null
																	// For WEEK frequency packs, filter to only show timings for selected weekdays
																	if (((selectedPricingPack?.pricingPack?.frequency === "WEEK" &&
																		selectedPricingPack?.pricingPack?.all === false) || isMonthDaysPerWeekPack(selectedPricingPack?.pricingPack)) &&
																		selectedWeekDays?.length &&
																		dayOrDate !== 'Common slots') {
																		const isRecurring = selectedBatch?.type === "recurring";
																		let shouldShow = false;

																		if (isRecurring) {
																			// For recurring batches, dayOrDate is day name (e.g., "monday")
																			shouldShow = selectedWeekDays.some(weekDay =>
																				weekDay.toLowerCase() === dayOrDate.toLowerCase()
																			);
																		} else {
																			// For specific batches, dayOrDate is formatted date (e.g., "12/11/2024")
																			// Check if this date's weekday matches selectedWeekDays
																			const dateMatch = selectedDays?.find((day) => {
																				const dayStr = timeutil.getInstance(day).format(Helper.dateFormat(firebase));
																				return dayStr === dayOrDate;
																			});
																			if (dateMatch) {
																				const dayName = timeutil.inFormat(dateMatch, "dddd").toUpperCase();
																				shouldShow = selectedWeekDays.includes(dayName);
																			}
																		}

																		if (!shouldShow) return null;
																	}

																	const hideSingleton =
																		timings.length === 1 &&
																		(dayOrDate !== 'Common slots' &&
																			selectedBatch?.type !== 'recurring' &&
																			selectedDays?.findIndex(
																				(d) => Helper.formatDate(d, firebase) === dayOrDate
																			) === -1);
																	if (hideSingleton) return null;

																	return (
																		<div key={dayOrDate} className="day-group">
																			{dayOrDate === 'Common slots' ? null : (
																				<div className="day-header">
																					{dayOrDate
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ')}
																				</div>
																			)}
																			<div className="global-flex slots-timings">
																				{timings.map((timing) => {
																					let availability = !readOnly ? getAvailabilityPerSlot(timing, activityId) : { text: "", color: "" }

																					// Check if slot is bookable based on enrollment rules
																					let isSlotBookableByEnrollmentRules = true;
																					let isClosedByEnrollment = false;
																					// Enrollment rules are enabled only if both duration and durationType are present
																					const isEnrollmentEnabled = enrollmentRules &&
																						enrollmentRules.duration != null &&
																						enrollmentRules.durationType &&
																						enrollmentRules.duration >= 0;
																					if (isEnrollmentEnabled && selectedDays?.length) {
																						const currentTime = timeutil.getEpoch(timeutil.getInstance());
																						const isRecurring = selectedBatch?.type === "recurring";

																						// For "Common slots", check if slot is bookable for ALL relevant selected days
																						// For WEEK frequency packs, only checks days matching selectedWeekDays
																						if (dayOrDate === "Common slots") {
																							// Get filtered days list (for WEEK packs, only includes matching weekdays)
																							const daysToCheck = getFilteredDaysList();

																							// Slot is bookable only if it's bookable for ALL relevant selected days
																							isSlotBookableByEnrollmentRules = daysToCheck.length > 0 && daysToCheck.every((day) => {
																								const dayOrDateStr = getDayOrDateString(day, isRecurring);
																								return isSlotBookableForDate(
																									timing,
																									day,
																									dayOrDateStr,
																									activityId,
																									enrollmentRules,
																									currentTime,
																									isRecurring
																								);
																							});
																							isClosedByEnrollment = !isSlotBookableByEnrollmentRules;
																						} else {
																							// For day-specific slots, find matching date and check bookability
																							const matchingDate = findMatchingDate(dayOrDate, isRecurring);
																							if (matchingDate) {
																								isSlotBookableByEnrollmentRules = isSlotBookableForDate(
																									timing,
																									matchingDate,
																									dayOrDate,
																									activityId,
																									enrollmentRules,
																									currentTime,
																									isRecurring
																								);
																								isClosedByEnrollment = !isSlotBookableByEnrollmentRules;
																							}
																						}
																					}

																					const isDisabled = !selectedDays?.length || !isSlotBookableByEnrollmentRules;

																					// Override availability text if closed by enrollment rules
																					let displayText = availability.text;
																					let displayColor = availability.color;
																					if (isClosedByEnrollment) {
																						displayText = "Booking closed";
																						displayColor = "#8D8D8D";
																					}

																					return (
																						<div
																							key={timing.key}
																							className={`timing-slot 
																					${isDisabled ?
																									"time-slot-disabled" : ""}
																				${selectedTimings[activityId]?.[dayOrDate]?.includes(timing.key)
																									? 'selected'
																									: ''
																								}`
																							}
																							onClick={() => {
																								if (readOnly || isDisabled) return;
																								handleTimingClick(
																									activityId,
																									dayOrDate,
																									timing.key,
																									false,
																									availability.text.toLowerCase()
																								);
																							}}

																							style={{
																								cursor: readOnly || isDisabled ? 'not-allowed' : undefined,
																								padding: "8px 10px",
																								opacity: isDisabled ? 0.5 : 1
																							}}
																						>
																							<p>
																								{timing?.label}
																								{displayText && <p className='avail-text' style={{ color: selectedTimings[activityId]?.[dayOrDate]?.includes(timing.key) ? "white" : displayColor }}> {displayText}</p>}
																							</p>

																						</div>
																					)
																				})}
																			</div>
																		</div>
																	);
																}
															)}
														</div>
													)
												}
												)}
											</div>
										</div>

									</div>
									: null}
							</div>
						</div>
					</Row> :
					null
			}
		</div >
	)
}

export default BookingCalendar